import SensorResource from '@/api/resources/sensor'
import {
  IDefrostTimeAlarmParams,
  IInterruptionAlarmParams,
  ILowLoadAlarmParams,
  IDoorOpenAlarmParams,
  IBurntResistorAlarmParams,
  IPeakHourAlarmParams,
  INoDataAlarmParams,
  IAlarmNoDataSettings,
  ITemperatureAlarmParams,
  ITemperatureSensorSettings,
  IDoorOpenSensorSettings,
  IEnergySensorSettings,
  IEmptyMixerAlarmParams,
  IOutsideRegularTimeParams,
  IPowerFactorAlarmParams,
  IAlarmProfileSettings,
  IOvervoltageAlarmParams,
} from '@/interfaces/api/sensor'
import { ISensor, IDoorOpenSensor, ITemperatureSensor, IEnergySensor } from '@/interfaces/entities/sensor'
import type {
  IDailyHourlyReadingsParams,
  IDailyHourlyReadingsResponse,
} from '@/interfaces/entities/daily-temperature'
import { buildDailyTemperatureCacheKey, buildDailyTemperatureCachePrefix } from '@/utils/daily-temperature/period-filter'
import { SensorType } from '@/interfaces/enums'
import { i18n } from '@/plugins/i18n'
import { defineStore } from 'pinia'
import { useSuccessStore } from './success'
import { useErrorStore } from './error'
import { ISchemaSensor } from '@/interfaces/entities/schema_sensor'
import { clearAllStores } from '@/services/clear-stores'
import { hasAlarmProfileForActivation } from '@/utils/sensor-alarm-profile'

interface IDailyTemperatureCacheEntry extends IDailyHourlyReadingsResponse {
  timestamp: number
}

type ISensorAlarmToggleDetails = IEnergySensor | ITemperatureSensor | IDoorOpenSensor

function rejectAlarmActivationWithoutProfile(
  isCurrentlyActive: boolean,
  profileId?: number | null,
  storedProfileId?: number | null,
): boolean {
  if (hasAlarmProfileForActivation(isCurrentlyActive, profileId, storedProfileId)) {
    return true
  }

  useErrorStore().setErrors('erro:', [i18n.global.t('sensor.alarms_config.profile_required_activate')])
  return false
}

function canProceedWithAlarmToggle(
  details: ISensorAlarmToggleDetails,
  profileId: number | undefined,
  activeKey: string,
  profileKey: keyof IAlarmProfileSettings,
): boolean {
  const isActive = (details.company_alarm_status as Record<string, boolean>)[activeKey] ?? false
  const storedProfileId = (details as IAlarmProfileSettings)[profileKey]
  return rejectAlarmActivationWithoutProfile(isActive, profileId, storedProfileId)
}

const CACHE_TTL = 5 * 60 * 1000 // 5 minutos em ms

const sensorTypes: { [key: string]: string } = {
  electricity: 'energy_sensors',
  temperature: 'temperature_sensors',
  door_open: 'open_sensors',
}

export const useSensorStore = defineStore({
  id: 'sensor',

  state: () => ({
    sensors: [] as ISensor[],
    sensorsByCompany: {} as Record<number, ISensor[]>,
    visibleSensors: {} as Record<number, boolean>,
    isSidebarOpen: false,
    selectedSensor: null as ISensor | null,
    selectedSensorDetails: null as IEnergySensor | ITemperatureSensor | IDoorOpenSensor | null,
    isLoadingSensorDetails: false,
    sidebarWidth: localStorage.getItem('sidebarWidth') || '600px',
    schemas: [] as ISchemaSensor[],
    dailyTemperatureCache: {} as Record<string, IDailyTemperatureCacheEntry>,
    isLoadingDailyTemperature: false,
    isLoadingMoreDailyTemperature: false,
  }),

  getters: {
    listSensors: (state) => state.sensors,
    getSensorsForCompany: (state) => (companyId: number) => state.sensorsByCompany[companyId] ?? [],
    listSchemas: (state) => state.schemas,
    isInterruptionAlarmActive: (state) => state.selectedSensorDetails?.company_alarm_status?.active_interruption_alarm ?? false,
    isLowLoadAlarmActive: (state) => state.selectedSensorDetails?.company_alarm_status?.active_low_load_alarm ?? false,
    isDefrostTimeAlarmActive: (state) => state.selectedSensorDetails?.company_alarm_status?.active_defrost_time_alarm ?? false,
    isBurntResistorAlarmActive: (state) => state.selectedSensorDetails?.company_alarm_status?.active_burnt_resistor_alarm ?? false,
    isDoorOpenAlarmActive: (state) => state.selectedSensorDetails?.company_alarm_status?.active_door_open_alarm ?? false,
    isPeakHourAlarmActive: (state) => state.selectedSensorDetails?.company_alarm_status?.active_peak_hour_alarm ?? false,
    isNoDataAlarmActive: (state) => state.selectedSensorDetails?.company_alarm_status?.active_no_data_alarm ?? false,
    isTemperatureAlarmActive: (state) => state.selectedSensorDetails?.company_alarm_status?.active_temperature_alarm ?? false,
    isEmptyMixerAlarmActive: (state) => state.selectedSensorDetails?.company_alarm_status?.active_empty_mixer_alarm ?? false,
    isOutsideRegularTimeActive: (state) => state.selectedSensorDetails?.company_alarm_status?.active_outside_regular_time_alarm ?? false,
    isPowerFactorAlarmActive: (state) => state.selectedSensorDetails?.company_alarm_status?.active_power_factor_alarm ?? false,
    isOvervoltageAlarmActive: (state) =>
      (state.selectedSensorDetails?.company_alarm_status as Record<string, boolean> | undefined)?.active_overvoltage_alarm ?? false,
    schemaSensor: (state) => state.selectedSensorDetails?.schema_sensor ?? null,

    getDailyTemperatureFromCache:
      (state) =>
      (params: IDailyHourlyReadingsParams): IDailyTemperatureCacheEntry | null => {
        const cacheKey = buildDailyTemperatureCacheKey(params)
        const entry = state.dailyTemperatureCache[cacheKey]
        if (!entry) return null

        const isExpired = Date.now() - entry.timestamp > CACHE_TTL
        if (isExpired) {
          delete state.dailyTemperatureCache[cacheKey]
          return null
        }

        return entry
      },

    hasCachedDailyTemperature:
      (state) =>
      (params: IDailyHourlyReadingsParams): boolean => {
        const cacheKey = buildDailyTemperatureCacheKey(params)
        const entry = state.dailyTemperatureCache[cacheKey]
        if (!entry) return false

        const isExpired = Date.now() - entry.timestamp > CACHE_TTL
        if (isExpired) {
          delete state.dailyTemperatureCache[cacheKey]
          return false
        }

        return true
      },
  },

  actions: {

    clearAllStores() {
      clearAllStores();
    },


    async openSidebar(sensor: ISensor) {
      this.selectedSensor = sensor
      this.isSidebarOpen = true
      this.selectedSensorDetails = null
      this.isLoadingSensorDetails = true

      try {
        this.selectedSensorDetails = await this.fetchSensorDetails(sensor.id, sensor.sensor_type)
      } finally {
        this.isLoadingSensorDetails = false
      }
    },

    closeSidebar() {
      this.isSidebarOpen = false
      this.selectedSensor = null
      this.selectedSensorDetails = null
    },

    async fetchSensorDetails(sensorId: number, sensorType: string) {
      try {
        let response
        switch (sensorType) {
          case 'electricity':
            response = await SensorResource.getEnergySensorsById(sensorId)
            break
          case 'temperature':
            response = await SensorResource.getTemperatureSensorsById(sensorId)
            break
          case 'door_open':
            response = await SensorResource.getOpenSensorsById(sensorId)
            break
          default:
            throw new Error('Sensor type not supported')
        }
        return response
      } catch {
        return null
      }
    },

    async fetchSchemas(sensorType: string): Promise<ISchemaSensor[]> {
      try {
        const list = (await SensorResource.getSchemaSensors(sensorType)) as ISchemaSensor[]
        this.schemas = list
        return list
      } catch {
        this.schemas = []
        return []
      }
    },

    async assignSchemaToSensor(schemaId: number, sensorId: number) {
      try {
        await SensorResource.assignToSensor(schemaId, sensorId)
        useSuccessStore().setSuccess(['Schema atribuido com sucesso ao sensor!'])

      } catch {
        // Erro ao atribuir schema ao sensor
      }

    },


    async fetch({ force = false } = {}) {
      if (this.sensors.length === 0 || force) {
        try {
          this.sensors = await SensorResource.getAllSensors()
        } catch {
          // Erro ao buscar sensores
        }
      }
    },

    async archiveSensor(sensorId: number) {
      try {
        const updated = (await SensorResource.archiveSensor(sensorId)) as ISensor
        const idx = this.sensors.findIndex((s) => s.id === sensorId)
        if (idx >= 0) this.sensors[idx] = { ...this.sensors[idx], ...updated }
        useSuccessStore().setSuccess(['Sensor desativado. Alarmes abertos foram encerrados.'])
      } catch (error) {
        useErrorStore().setErrors([(error as Error).message || i18n.global.t('sensor.errors.archive_failed')])
        throw error
      }
    },

    async unarchiveSensor(sensorId: number) {
      try {
        const updated = (await SensorResource.unarchiveSensor(sensorId)) as ISensor
        const idx = this.sensors.findIndex((s) => s.id === sensorId)
        if (idx >= 0) this.sensors[idx] = { ...this.sensors[idx], ...updated }
        useSuccessStore().setSuccess(['Sensor reativado. Reative os alarmes individualmente quando desejar.'])
      } catch (error) {
        useErrorStore().setErrors([(error as Error).message || i18n.global.t('sensor.errors.unarchive_failed')])
        throw error
      }
    },

    async fetchSensorsByCompanyId(companyId: number): Promise<ISensor[]> {
      try {
        const data = await SensorResource.getSensorsIndexedByCompany(companyId)
        this.sensorsByCompany = { ...this.sensorsByCompany, [companyId]: data }
        return data
      } catch {
        useErrorStore().setErrors([i18n.global.t('sensor.errors.fetch_failed')])
      }
    },

    async toggleInterruptionAlarm(sensorId: number, profileId?: number) {
      if (!this.selectedSensorDetails) return

      if (
        !canProceedWithAlarmToggle(
          this.selectedSensorDetails,
          profileId,
          'active_interruption_alarm',
          'alarm_interrupt_sensor_id',
        )
      ) {
        return
      }

      try {
        this.selectedSensorDetails.company_alarm_status.active_interruption_alarm = !this.selectedSensorDetails.company_alarm_status.active_interruption_alarm

        if (profileId !== undefined) {
          this.selectedSensorDetails.alarm_interrupt_sensor_id = profileId
        }

        const params: IInterruptionAlarmParams = {
          active_interruption_alarm: this.selectedSensorDetails.company_alarm_status.active_interruption_alarm,
          last_interruption_alarm_date: null,
          alarm_interrupt_sensor_id: profileId ?? this.selectedSensorDetails.alarm_interrupt_sensor_id,
        }

        await SensorResource.activeInterruptionAlarm(sensorId, params)
      } catch (error) {
        this.selectedSensorDetails.company_alarm_status.active_interruption_alarm = !this.selectedSensorDetails.company_alarm_status.active_interruption_alarm
      }
    },

    async toggleDoorOpenAlarm(sensorId: number, profileId?: number) {
      if (!this.selectedSensorDetails) return

      if (
        !canProceedWithAlarmToggle(
          this.selectedSensorDetails,
          profileId,
          'active_door_open_alarm',
          'alarm_door_open_sensor_id',
        )
      ) {
        return
      }

      try {
        this.selectedSensorDetails.company_alarm_status.active_door_open_alarm = !this.selectedSensorDetails.company_alarm_status.active_door_open_alarm

        if (profileId !== undefined) {
          this.selectedSensorDetails.alarm_door_open_sensor_id = profileId
        }

        const params: IDoorOpenAlarmParams = {
          active_door_open_alarm: this.selectedSensorDetails.company_alarm_status.active_door_open_alarm,
          last_door_open_alarm_date: null,
          alarm_door_open_sensor_id: profileId ?? this.selectedSensorDetails.alarm_door_open_sensor_id,
        }

        await SensorResource.activateDoorOpenAlarm(sensorId, params)
      } catch (error) {
        this.selectedSensorDetails.company_alarm_status.active_door_open_alarm = !this.selectedSensorDetails.company_alarm_status.active_door_open_alarm
      }
    },

    async toggleLowLoadAlarm(sensorId: number, profileId?: number) {
      if (!this.selectedSensorDetails) return

      if (
        !canProceedWithAlarmToggle(
          this.selectedSensorDetails,
          profileId,
          'active_low_load_alarm',
          'alarm_low_load_sensor_id',
        )
      ) {
        return
      }

      try {
        this.selectedSensorDetails.company_alarm_status.active_low_load_alarm = !this.selectedSensorDetails.company_alarm_status.active_low_load_alarm

        if (profileId !== undefined) {
          this.selectedSensorDetails.alarm_low_load_sensor_id = profileId
        }

        const params: ILowLoadAlarmParams = {
          active_low_load_alarm: this.selectedSensorDetails.company_alarm_status.active_low_load_alarm,
          last_low_load_alarm_date: null,
          alarm_low_load_sensor_id: profileId ?? this.selectedSensorDetails.alarm_low_load_sensor_id,
        }

        await SensorResource.activateLowLoadAlarm(sensorId, params)
      } catch (error) {
        this.selectedSensorDetails.company_alarm_status.active_low_load_alarm = !this.selectedSensorDetails.company_alarm_status.active_low_load_alarm
      }
    },

    async toggleDefrostTimeAlarm(sensorId: number, profileId?: number) {
      if (!this.selectedSensorDetails) return

      if (
        !canProceedWithAlarmToggle(
          this.selectedSensorDetails,
          profileId,
          'active_defrost_time_alarm',
          'alarm_defrost_sensor_id',
        )
      ) {
        return
      }

      try {
        this.selectedSensorDetails.company_alarm_status.active_defrost_time_alarm = !this.selectedSensorDetails.company_alarm_status.active_defrost_time_alarm

        if (profileId !== undefined) {
          this.selectedSensorDetails.alarm_defrost_sensor_id = profileId
        }

        const params: IDefrostTimeAlarmParams = {
          active_defrost_time_alarm: this.selectedSensorDetails.company_alarm_status.active_defrost_time_alarm,
          alarm_defrost_sensor_id: profileId ?? this.selectedSensorDetails.alarm_defrost_sensor_id,
        }

        await SensorResource.activateDefrostTimeAlarm(sensorId, params)
      } catch (error) {
        this.selectedSensorDetails.company_alarm_status.active_defrost_time_alarm = !this.selectedSensorDetails.company_alarm_status.active_defrost_time_alarm
      }
    },

    async toggleBurntResistorAlarm(sensorId: number, profileId?: number) {
      if (!this.selectedSensorDetails) return

      if (
        !canProceedWithAlarmToggle(
          this.selectedSensorDetails,
          profileId,
          'active_burnt_resistor_alarm',
          'alarm_defrost_sensor_id',
        )
      ) {
        return
      }

      try {
        this.selectedSensorDetails.company_alarm_status.active_burnt_resistor_alarm =
          !this.selectedSensorDetails.company_alarm_status.active_burnt_resistor_alarm

        if (profileId !== undefined) {
          this.selectedSensorDetails.alarm_defrost_sensor_id = profileId
        }

        const params: IBurntResistorAlarmParams = {
          active_burnt_resistor_alarm: this.selectedSensorDetails.company_alarm_status.active_burnt_resistor_alarm,
          alarm_defrost_sensor_id: profileId ?? this.selectedSensorDetails.alarm_defrost_sensor_id,
        }

        await SensorResource.activateBurntResistorAlarm(sensorId, params)
      } catch (error) {
        this.selectedSensorDetails.company_alarm_status.active_burnt_resistor_alarm =
          !this.selectedSensorDetails.company_alarm_status.active_burnt_resistor_alarm
      }
    },

    async togglePeakHourAlarm(sensorId: number, profileId?: number) {
      if (!this.selectedSensorDetails) return

      if (
        !canProceedWithAlarmToggle(
          this.selectedSensorDetails,
          profileId,
          'active_peak_hour_alarm',
          'alarm_peak_hour_sensor_id',
        )
      ) {
        return
      }

      try {
        this.selectedSensorDetails.company_alarm_status.active_peak_hour_alarm = !this.selectedSensorDetails.company_alarm_status.active_peak_hour_alarm

        if (profileId !== undefined) {
          this.selectedSensorDetails.alarm_peak_hour_sensor_id = profileId
        }

        const params: IPeakHourAlarmParams = {
          active_peak_hour_alarm: this.selectedSensorDetails.company_alarm_status.active_peak_hour_alarm,
          alarm_peak_hour_sensor_id: profileId ?? this.selectedSensorDetails.alarm_peak_hour_sensor_id,
        }

        await SensorResource.activatePeakHourAlarm(sensorId, params)
      } catch (error) {
        this.selectedSensorDetails.company_alarm_status.active_peak_hour_alarm = !this.selectedSensorDetails.company_alarm_status.active_peak_hour_alarm
      }
    },

    async toggleNoDataAlarm(sensorId: number, sensorType: string, profileId?: number) {
      const sensorTypePath = sensorTypes[sensorType]

      if (!this.selectedSensorDetails) return

      if (
        !canProceedWithAlarmToggle(
          this.selectedSensorDetails,
          profileId,
          'active_no_data_alarm',
          'alarm_no_data_sensor_id',
        )
      ) {
        return
      }

      try {

        this.selectedSensorDetails.company_alarm_status.active_no_data_alarm = !this.selectedSensorDetails.company_alarm_status.active_no_data_alarm

        if (profileId !== undefined) {
          this.selectedSensorDetails.alarm_no_data_sensor_id = profileId
        }

        const params: INoDataAlarmParams = {
          active_no_data_alarm: this.selectedSensorDetails.company_alarm_status.active_no_data_alarm,
          last_no_data_alarm_date: null,
          alarm_no_data_sensor_id: profileId ?? this.selectedSensorDetails.alarm_no_data_sensor_id,
        }

        await SensorResource.activateNoDataAlarm(sensorId, sensorTypePath, params)
      } catch (error) {
        this.selectedSensorDetails.company_alarm_status.active_no_data_alarm = !this.selectedSensorDetails.company_alarm_status.active_no_data_alarm
      }
    },

    async togglePowerFactorAlarm(sensorId: number, profileId?: number) {
      if (!this.selectedSensorDetails) return

      if (
        !canProceedWithAlarmToggle(
          this.selectedSensorDetails,
          profileId,
          'active_power_factor_alarm',
          'alarm_power_factor_sensor_id',
        )
      ) {
        return
      }

      try {
        this.selectedSensorDetails.company_alarm_status.active_power_factor_alarm = !this.selectedSensorDetails.company_alarm_status.active_power_factor_alarm

        if (profileId !== undefined) {
          this.selectedSensorDetails.alarm_power_factor_sensor_id = profileId
        }

        const params: IPowerFactorAlarmParams = {
          active_power_factor_alarm: this.selectedSensorDetails.company_alarm_status.active_power_factor_alarm,
          last_power_factor_alarm_date: null,
          alarm_power_factor_sensor_id: profileId ?? this.selectedSensorDetails.alarm_power_factor_sensor_id,
        }

        await SensorResource.activatePowerFactorAlarm(sensorId, params)
      } catch (error) {
        this.selectedSensorDetails.company_alarm_status.active_power_factor_alarm = !this.selectedSensorDetails.company_alarm_status.active_power_factor_alarm
        useErrorStore().setErrors(error.message || i18n.global.t('sensor.errors.update_alarm_failed'))
      }
    },

    async toggleOvervoltageAlarm(sensorId: number, profileId?: number) {
      if (!this.selectedSensorDetails) return

      if (
        !canProceedWithAlarmToggle(
          this.selectedSensorDetails,
          profileId,
          'active_overvoltage_alarm',
          'alarm_overvoltage_sensor_id',
        )
      ) {
        return
      }

      try {
        this.selectedSensorDetails.company_alarm_status.active_overvoltage_alarm = !this.selectedSensorDetails.company_alarm_status.active_overvoltage_alarm

        if (profileId !== undefined) {
          this.selectedSensorDetails.alarm_overvoltage_sensor_id = profileId
        }

        const params: IOvervoltageAlarmParams = {
          active_overvoltage_alarm: this.selectedSensorDetails.company_alarm_status.active_overvoltage_alarm,
          last_overvoltage_alarm_date: null,
          alarm_overvoltage_sensor_id: profileId ?? this.selectedSensorDetails.alarm_overvoltage_sensor_id,
        }

        await SensorResource.activateOvervoltageAlarm(sensorId, params)
      } catch (error) {
        this.selectedSensorDetails.company_alarm_status.active_overvoltage_alarm = !this.selectedSensorDetails.company_alarm_status.active_overvoltage_alarm
        useErrorStore().setErrors(error.message || i18n.global.t('sensor.errors.update_alarm_failed'))
      }
    },

    async toggleTemperatureAlarm(sensorId: number, profileId?: number) {
      if (!this.selectedSensorDetails) return

      if (
        !canProceedWithAlarmToggle(
          this.selectedSensorDetails,
          profileId,
          'active_temperature_alarm',
          'alarm_critical_temperature_sensor_id',
        )
      ) {
        return
      }

      try {
        this.selectedSensorDetails.company_alarm_status.active_temperature_alarm = !this.selectedSensorDetails.company_alarm_status.active_temperature_alarm

        if (profileId !== undefined) {
          this.selectedSensorDetails.alarm_critical_temperature_sensor_id = profileId
        }

        const params: ITemperatureAlarmParams = {
          active_temperature_alarm: this.selectedSensorDetails.company_alarm_status.active_temperature_alarm,
          last_temperature_alarm_date: null,
          alarm_critical_temperature_sensor_id: profileId ?? this.selectedSensorDetails.alarm_critical_temperature_sensor_id,
        }

        await SensorResource.activateTemperatureAlarm(sensorId, params)
      } catch (error) {
        this.selectedSensorDetails.company_alarm_status.active_temperature_alarm = !this.selectedSensorDetails.company_alarm_status.active_temperature_alarm
      }
    },

    async toggleEmptyMixerAlarm(sensorId: number, profileId?: number) {
      if (!this.selectedSensorDetails) return

      if (
        !canProceedWithAlarmToggle(
          this.selectedSensorDetails,
          profileId,
          'active_empty_mixer_alarm',
          'alarm_empty_mixer_sensor_id',
        )
      ) {
        return
      }

      try {
        this.selectedSensorDetails.company_alarm_status.active_empty_mixer_alarm = !this.selectedSensorDetails.company_alarm_status.active_empty_mixer_alarm

        if (profileId !== undefined) {
          this.selectedSensorDetails.alarm_empty_mixer_sensor_id = profileId
        }

        const params: IEmptyMixerAlarmParams = {
          active_empty_mixer_alarm: this.selectedSensorDetails.company_alarm_status.active_empty_mixer_alarm,
          alarm_empty_mixer_sensor_id: profileId ?? this.selectedSensorDetails.alarm_empty_mixer_sensor_id,
        }

        await SensorResource.activateEmptyMixerAlarm(sensorId, params)
      } catch (error) {
        this.selectedSensorDetails.company_alarm_status.active_empty_mixer_alarm = !this.selectedSensorDetails.company_alarm_status.active_empty_mixer_alarm
      }
    },

    async toggleIOutsideRegularTimeAlarm(sensorId: number, profileId?: number) {
      if (!this.selectedSensorDetails) return

      if (
        !canProceedWithAlarmToggle(
          this.selectedSensorDetails,
          profileId,
          'active_outside_regular_time_alarm',
          'alarm_outside_regular_sensor_id',
        )
      ) {
        return
      }

      try {
        this.selectedSensorDetails.company_alarm_status.active_outside_regular_time_alarm = !this.selectedSensorDetails.company_alarm_status.active_outside_regular_time_alarm

        if (profileId !== undefined) {
          this.selectedSensorDetails.alarm_outside_regular_sensor_id = profileId
        }

        const params: IOutsideRegularTimeParams = {
          active_outside_regular_time_alarm: this.selectedSensorDetails.company_alarm_status.active_outside_regular_time_alarm,
          last_outside_regular_time_alarm_date: null,
          alarm_outside_regular_sensor_id: profileId ?? this.selectedSensorDetails.alarm_outside_regular_sensor_id,
        }

        await SensorResource.activateOutsideRegularTimeAlarm(sensorId, params)
      } catch (error) {
        this.selectedSensorDetails.company_alarm_status.active_outside_regular_time_alarm = !this.selectedSensorDetails.company_alarm_status.active_outside_regular_time_alarm
      }
    },

    async updateInterruptionSettings(sensorId: number, profileId?: number) {
      const alarm_interrupt_sensor_id = profileId
      await SensorResource.alarmProfileSettings(sensorId, { alarm_interrupt_sensor_id })
      useSuccessStore().setSuccess(['Configurações atualizadas com sucesso!'])
    },

    async updateLowLoadSettings(sensorId: number, profileId?: number) {
      await SensorResource.alarmProfileSettings(sensorId, { alarm_low_load_sensor_id: profileId })
      useSuccessStore().setSuccess(['Configurações atualizadas com sucesso!'])
    },

    async updateDoorOpenSettings(sensorId: number, profileId?: number) {
      await SensorResource.alarmDoorOpenSettings(sensorId, { alarm_door_open_sensor_id: profileId })
      useSuccessStore().setSuccess(['Configurações atualizadas com sucesso!'])
    },

    async updateEnergySensorInfo(sensorId: number, settings: IEnergySensorSettings) {
      await SensorResource.updateEnergySensor(sensorId, settings)
      useSuccessStore().setSuccess(['Configurações atualizadas com sucesso!'])
    },

    async updateTemperatureSensorInfo(sensorId: number, settings: ITemperatureSensorSettings) {
      await SensorResource.updateTemperatureSensor(sensorId, settings)
      useSuccessStore().setSuccess(['Configurações atualizadas com sucesso!'])
    },

    async updateDoorOpenSensorInfo(sensorId: number, settings: IDoorOpenSensorSettings) {
      await SensorResource.updateDoorOpenSensor(sensorId, settings)
      useSuccessStore().setSuccess(['Configurações atualizadas com sucesso!'])
    },

    async updatePeakHourSettings(sensorId: number, profileId?: number) {
      await SensorResource.alarmProfileSettings(sensorId, { alarm_peak_hour_sensor_id: profileId })
      useSuccessStore().setSuccess(['Configurações atualizadas com sucesso!'])
    },

    async updateDefrostTimeSettings(sensorId: number, profileId?: number) {
      await SensorResource.alarmProfileSettings(sensorId, { alarm_defrost_sensor_id: profileId })
      useSuccessStore().setSuccess(['Configurações atualizadas com sucesso!'])
    },

    updateSidebarWidth(newWidth: string) {
      this.sidebarWidth = newWidth
      localStorage.setItem('sidebarWidth', newWidth)
    },

    async updateBurntResistorSettings(sensorId: number, profileId?: number) {
      await SensorResource.alarmProfileSettings(sensorId, { alarm_defrost_sensor_id: profileId })
      useSuccessStore().setSuccess(['Configurações atualizadas com sucesso!'])
    },

    async updateNoDataSettings(sensorId: number, sensorType: string, settings: IAlarmNoDataSettings) {
      const sensorTypePath = sensorTypes[sensorType]
      await SensorResource.alarmNoDataSettings(sensorId, sensorTypePath, settings)
      useSuccessStore().setSuccess(['Configurações atualizadas com sucesso!'])
    },

    async updateTemperatureSettings(sensorId: number, profileId?: number) {
      await SensorResource.alarmProfileTemperatureSettings(sensorId, { alarm_critical_temperature_sensor_id: profileId })
      useSuccessStore().setSuccess(['Configurações atualizadas com sucesso!'])
    },

    async updateEmptyMixerSettings(sensorId: number, profileId?: number) {
      await SensorResource.alarmProfileSettings(sensorId, { alarm_empty_mixer_sensor_id: profileId })
      useSuccessStore().setSuccess(['Configurações atualizadas com sucesso!'])
    },

    async updateIOutsideRegularTimeSettings(sensorId: number, profileId?: number) {
      await SensorResource.alarmProfileSettings(sensorId, { alarm_outside_regular_sensor_id: profileId })
      useSuccessStore().setSuccess(['Configurações atualizadas com sucesso!'])
    },

    async updatePowerFactorSettings(sensorId: number, profileId?: number) {
      await SensorResource.alarmProfileSettings(sensorId, { alarm_power_factor_sensor_id: profileId })
      useSuccessStore().setSuccess(['Configurações atualizadas com sucesso!'])
    },

    async updateOvervoltageSettings(sensorId: number, profileId?: number) {
      await SensorResource.alarmProfileSettings(sensorId, { alarm_overvoltage_sensor_id: profileId })
      useSuccessStore().setSuccess(['Configurações atualizadas com sucesso!'])
    },


    async fetchDailyTemperature(
      params: IDailyHourlyReadingsParams,
      forceRefresh = false,
    ): Promise<IDailyHourlyReadingsResponse> {
      const cacheKey = buildDailyTemperatureCacheKey(params)

      if (!forceRefresh) {
        const cached = this.dailyTemperatureCache[cacheKey]
        if (cached) {
          const isExpired = Date.now() - cached.timestamp > CACHE_TTL
          if (!isExpired) {
            return cached
          }
          delete this.dailyTemperatureCache[cacheKey]
        }
      }

      try {
        const response = await SensorResource.getDailyHourlyTemperature(params)

        this.dailyTemperatureCache[cacheKey] = {
          ...response,
          timestamp: Date.now(),
        }

        return response
      } catch {
        useErrorStore().setErrors([i18n.global.t('sensor.errors.fetch_daily_temperature_failed')])
        throw new Error(i18n.global.t('sensor.errors.fetch_daily_temperature_failed'))
      }
    },

    invalidateDailyTemperatureCache(params?: IDailyHourlyReadingsParams) {
      if (params) {
        const prefix = buildDailyTemperatureCachePrefix(params)
        Object.keys(this.dailyTemperatureCache).forEach((key) => {
          if (key.startsWith(`${prefix}|`)) {
            delete this.dailyTemperatureCache[key]
          }
        })
        return
      }

      this.dailyTemperatureCache = {}
    },

    hasMorePagesInCache(params: IDailyHourlyReadingsParams): boolean {
      const nextParams = {
        ...params,
        page: (params.page ?? 1) + 1,
      }
      const nextPageKey = buildDailyTemperatureCacheKey(nextParams)
      const entry = this.dailyTemperatureCache[nextPageKey]
      if (!entry) {
        const current = this.dailyTemperatureCache[buildDailyTemperatureCacheKey(params)]
        return current?.pagy?.next != null
      }

      const isExpired = Date.now() - entry.timestamp > CACHE_TTL
      if (isExpired) {
        delete this.dailyTemperatureCache[nextPageKey]
        return false
      }

      return entry.pagy?.next != null
    },
  },
})
